/**
 * @fileoverview Hook for managing operator queue data
 * Provides functionality to fetch operators with their assigned tasks and manage priorities
 */

import { useState, useCallback } from 'react';
import { pb } from '../helpers/pocketbase_helper';
import { LogPlanCentroCosto } from '../types/collections/LogPlanCentroCosto';

/**
 * Interface for operator with tasks
 */
export interface OperarioConTareas {
  id: string;
  name: string;
  email: string;
  rol: {
    nombre: string;
  };
  tareas: TareaOperario[];
}

/**
 * Interface for operator task
 */
export interface TareaOperario extends LogPlanCentroCosto {
  expand?: {
    orden?: {
      id: string;
      numero: number;
      nombre_producto: string;
    };
    centro_costo?: {
      id: string;
      nombre: string;
    };
  };
}

/**
 * Interface for priority update
 */
export interface TareaReordenada {
  id: string;
  prioridad: number;
}

/**
 * Hook for managing operator queue operations
 * @param tipoCola - Type of queue: 'interno' for operators, 'externo' for external providers
 * @returns Object with state and functions for operator queue management
 */
export const useColaOperarios = (tipoCola: 'interno' | 'externo' = 'interno') => {
  // State management
  const [operarios, setOperarios] = useState<OperarioConTareas[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError("");
  }, []);

  /**
   * Set loading state
   */
  const setLoadingState = useCallback((loading: boolean) => {
    setLoading(loading);
  }, []);

  /**
   * Set error state
   */
  const setErrorState = useCallback((error: string) => {
    setError(error);
  }, []);

  /**
   * Fetch operators with their assigned tasks
   */
  const fetchOperariosConTareas = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      // Build filter based on tipoCola - get ALL tasks (both ready and not ready)
      let filter = '(fin = "" || fin = null)'; // Only exclude completed tasks
      if (tipoCola === 'externo') {
        filter += ' && proceso_proveedor != "" && operario = ""';
      } else {
        filter += ' && operario != ""';
      }

      // First, get all tasks (both ready and not ready) to find unique IDs
      const tareasResult = await pb.collection('log_plan_centro_costo').getList(1, 1000, {
        filter,
        expand: 'orden,centro_costo,log_plan,tarea_orden,proceso_proveedor',
        sort: 'prioridad'
        // Removed fields parameter to allow expand data to be included
      });


      // Get unique IDs based on tipoCola
      let entityIds: string[] = [];
      if (tipoCola === 'externo') {
        entityIds = Array.from(new Set(tareasResult.items.map(tarea => tarea.proceso_proveedor)));
      } else {
        entityIds = Array.from(new Set(tareasResult.items.map(tarea => tarea.operario)));
      }

      // Fetch each entity individually
      const entidades = [];
      for (const entityId of entityIds) {
        try {
          let entidad;
          if (tipoCola === 'externo') {
            entidad = await pb.collection('proceso_proveedor').getOne(entityId, {
              expand: 'rol'
            });
          } else {
            entidad = await pb.collection('users').getOne(entityId, {
              expand: 'rol'
            });
          }
          entidades.push(entidad);
        } catch (err) {
          console.warn(`No se pudo obtener entidad ${entityId}:`, err);
        }
      }

      // Create entidadesResult
      const entidadesResult = {
        items: entidades
      };

      // Group tasks by entity
      const operariosConTareas: OperarioConTareas[] = entidadesResult.items.map(entidad => {
        const tareasDeLaEntidad = tareasResult.items
          .filter((tarea: any) => {
            if (tipoCola === 'externo') {
              return tarea.proceso_proveedor === entidad.id;
            } else {
              return tarea.operario === entidad.id;
            }
          })
        .sort((a: any, b: any) => {
          // Primero: tareas ready (asignadas y listas) van primero
          const aReady = a.ready === true;
          const bReady = b.ready === true;
          
          if (aReady && !bReady) return -1; // a va primero
          if (!aReady && bReady) return 1;  // b va primero
          
          // Si ambas tienen el mismo estado (ready o no ready), ordenar por prioridad
          const prioridadA = a.prioridad || 1;
          const prioridadB = b.prioridad || 1;
          
          if (prioridadA !== prioridadB) {
            return prioridadA - prioridadB;
          }
          
          // Si tienen la misma prioridad, ordenar por orden_plan
          const ordenPlanA = a.orden_plan || 0;
          const ordenPlanB = b.orden_plan || 0;
          
          if (ordenPlanA !== ordenPlanB) {
            return ordenPlanA - ordenPlanB;
          }
          
          // Si tienen el mismo orden_plan, ordenar por número de orden
          const ordenA = a.expand?.orden_produccion?.numero || 0;
          const ordenB = b.expand?.orden_produccion?.numero || 0;
          return ordenA - ordenB;
        })
          .map((tarea: any, index: number): TareaOperario => ({
            id: tarea.id,
            collectionId: tarea.collectionId || 'pbc_1909650630',
            collectionName: tarea.collectionName || 'log_plan_centro_costo',
            orden: tarea.orden,
            centro_costo: tarea.centro_costo,
            operario: tarea.operario,
            proceso_proveedor: tarea.proceso_proveedor,
            prioridad: tarea.prioridad || 1, // Mantener prioridad original de la base de datos
            ready: tarea.ready,
            inicio: tarea.inicio,
            fin: tarea.fin,
            log_plan: tarea.log_plan, // Agregar campo log_plan
            tarea_orden: tarea.tarea_orden, // Agregar campo tarea_orden
            created: tarea.created,
            updated: tarea.updated,
            expand: tarea.expand
          }))
          .map((tarea: TareaOperario, index: number) => ({
            ...tarea,
            // Mantener la prioridad original de la base de datos, no sobrescribir con index + 1
            prioridad: tarea.prioridad || (index + 1) // Solo usar index + 1 si no hay prioridad
          }));

        return {
          id: entidad.id,
          name: entidad.name,
          email: entidad.email || '',
          rol: entidad.expand?.rol || { nombre: tipoCola === 'externo' ? 'proveedor' : 'operario' },
          tareas: tareasDeLaEntidad
        };
      });

      setOperarios(operariosConTareas);
      
      // Actualizar prioridades en la base de datos si es necesario
      await actualizarPrioridadesSiEsNecesario(operariosConTareas);
      
    } catch (err: any) {
      console.error("Error fetching operarios con tareas:", err);
      console.error("Error details:", {
        message: err.message,
        status: err.status,
        data: err.data,
        response: err.response
      });
      setError("Error al cargar operarios y tareas");
    } finally {
      setLoading(false);
    }
  }, [tipoCola]);

  /**
   * Actualizar prioridades en la base de datos si es necesario
   */
  const actualizarPrioridadesSiEsNecesario = async (operariosConTareas: OperarioConTareas[]) => {
    try {
      for (const operario of operariosConTareas) {
        if (operario.tareas && operario.tareas.length > 0) {
          // Verificar si las prioridades están correctas
          const prioridadesCorrectas = operario.tareas.every((tarea, index) => tarea.prioridad === index + 1);
          
          if (!prioridadesCorrectas) {
            console.log(`Actualizando prioridades para operario ${operario.name}`);
            
            // Actualizar cada tarea con su prioridad correcta
            for (let i = 0; i < operario.tareas.length; i++) {
              const tarea = operario.tareas[i];
              const prioridadCorrecta = i + 1;
              
              if (tarea.prioridad !== prioridadCorrecta) {
                await pb.collection('log_plan_centro_costo').update(tarea.id, {
                  prioridad: prioridadCorrecta
                });
                console.log(`Tarea ${tarea.id} actualizada con prioridad ${prioridadCorrecta}`);
              }
            }
          }
        }
      }
    } catch (error) {
      console.error("Error actualizando prioridades:", error);
    }
  };

  /**
   * Update task priorities for an operator
   */
  const updatePrioridades = useCallback(async (
    operarioId: string, 
    tareasReordenadas: TareaReordenada[]
  ) => {
    try {
      setLoading(true);
      setError("");

      // Update each task priority
      for (const tarea of tareasReordenadas) {
        await pb.collection('log_plan_centro_costo').update(tarea.id, {
          prioridad: tarea.prioridad
        });
      }

      // Refresh data
      await fetchOperariosConTareas();
    } catch (err: any) {
      console.error("Error updating prioridades:", err);
      setError("Error al actualizar prioridades");
    } finally {
      setLoading(false);
    }
  }, [fetchOperariosConTareas]);

  return {
    // State
    operarios,
    setOperarios,
    loading,
    error,
    
    // Actions
    fetchOperariosConTareas,
    updatePrioridades,
    
    // Utilities
    clearError,
    setLoading: setLoadingState,
    setError: setErrorState
  };
};
